import React, { useState, useEffect, useCallback } from 'react';
import { adminGetProducts, adminUpdateProduct, getFullImageUrl, API_URL } from '../services/api';
import './StockManager.css';

interface Variation {
  color: string;
  colorCode: string;
  images: string[];
  stockQuantity?: number;
  additionalPrice: number;
  isDefault?: boolean;
  sizes?: UKShoeSize[]; 
  startQuantity: number;
  totalAcquired: number;
  damagedQuantity: number;
  returnedToStock: number;
}

interface UKShoeSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
  startQuantity: number;
  totalAcquired: number;
  damagedQuantity: number;
  returnedToStock: number;
}

interface Product {
  _id: string;
  productId: string;
  name: string;
  description: string;
  price: number;
  costPrice: number;
  image: string;
  variations: Variation[];
  category: string;
  inStock: boolean;
  stockQuantity: number;
  ukShoeSizes?: UKShoeSize[];
  discount?: number;
  startQuantity: number;
  totalAcquired: number;
  damagedQuantity: number;
  returnedToStock: number;
}

interface StockHistoryEntryInput {
  productId: string;
  productName: string;
  productIdentifier: string;
  type: 'size' | 'variation' | 'base';
  identifier: string;
  oldQuantity: number;
  newQuantity: number;
  change: number;
  reason: string;
  returnQuality?: 'good' | 'bad';
}

interface StockHistoryEntry extends StockHistoryEntryInput {
  date: Date;
  adminName: string;
}

interface ReturnItem {
  id: string;
  productId: string;
  productName: string;
  productIdentifier: string;
  quantity: number;
  quality: 'good' | 'bad';
  reason: string;
  date: Date;
  processedBy: string;
  orderReference?: string;
}

const StockManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stockHistory, setStockHistory] = useState<StockHistoryEntry[]>([]);
  const [returns, setReturns] = useState<ReturnItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showReturns, setShowReturns] = useState(false);
  const [editingStock, setEditingStock] = useState<{
    productId: string;
    type: 'base' | 'variation' | 'size' | 'damaged';
    variationIndex?: number;
    sizeIndex?: number;
    currentStock: number;
  } | null>(null);
  const [newStockValue, setNewStockValue] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnItem, setReturnItem] = useState<{
    productId: string;
    productName: string;
    productIdentifier: string;
    type: 'base' | 'variation' | 'size';
    variationIndex?: number;
    sizeIndex?: number;
    currentStock: number;
  } | null>(null);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [returnQuality, setReturnQuality] = useState<'good' | 'bad'>('good');
  const [returnReason, setReturnReason] = useState('');
  const [showStartQuantityModal, setShowStartQuantityModal] = useState(false);
  const [startQuantityProduct, setStartQuantityProduct] = useState<Product | null>(null);
  const [startQuantityValue, setStartQuantityValue] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');

  // Load stock history and returns from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('stockHistory');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        setStockHistory(parsed.map((entry: any) => ({ ...entry, date: new Date(entry.date) })));
      } catch (e) {
        console.error('Failed to load stock history:', e);
      }
    }
    
    const savedReturns = localStorage.getItem('productReturns');
    if (savedReturns) {
      try {
        const parsed = JSON.parse(savedReturns);
        setReturns(parsed.map((entry: any) => ({ ...entry, date: new Date(entry.date) })));
      } catch (e) {
        console.error('Failed to load returns:', e);
      }
    }
  }, []);

  // Save stock history to localStorage
  const saveToHistory = (entry: StockHistoryEntryInput) => {
    const fullEntry: StockHistoryEntry = {
      ...entry,
      date: new Date(),
      adminName: localStorage.getItem('userName') || 'Admin'
    };
    const updatedHistory = [fullEntry, ...stockHistory].slice(0, 100);
    setStockHistory(updatedHistory);
    localStorage.setItem('stockHistory', JSON.stringify(updatedHistory));
  };

  // Save return to localStorage
  const saveReturn = (returnItem: ReturnItem) => {
    const updatedReturns = [returnItem, ...returns].slice(0, 200);
    setReturns(updatedReturns);
    localStorage.setItem('productReturns', JSON.stringify(updatedReturns));
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminGetProducts();
      if (response.data.success) {
        const productsData = response.data.products || [];
        const enhancedProducts = productsData.map((product: any) => {
          // Initialize variation/size level stock tracking
          const enhancedVariations = (product.variations || []).map((v: any) => ({
            ...v,
            startQuantity: v.startQuantity || 0,
            totalAcquired: v.totalAcquired || 0,
            damagedQuantity: v.damagedQuantity || 0,
            returnedToStock: v.returnedToStock || 0,
            sizes: (v.sizes || []).map((s: any) => ({
              ...s,
              startQuantity: s.startQuantity || s.stockQuantity || 0,
              totalAcquired: s.totalAcquired || s.stockQuantity || 0,
              damagedQuantity: s.damagedQuantity || 0,
              returnedToStock: s.returnedToStock || 0,
            }))
          }));
          
          return {
            ...product,
            productId: product._id,
            costPrice: product.costPrice || 0,
            variations: enhancedVariations
          };
        });
        setProducts(enhancedProducts);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const updateStock = async (
    productId: string,
    updateData: any,
    entry: StockHistoryEntryInput
  ) => {
    try {
      // ✅ REMOVE protected fields from updateData
      const cleanUpdateData = { ...updateData };
      delete cleanUpdateData.startQuantity;
      
      // ✅ Only remove totalAcquired if it's NOT being explicitly set
      // If totalAcquired is being updated (new shipment), keep it
      if (updateData.totalAcquired === undefined) {
        delete cleanUpdateData.totalAcquired;
      }
      
      const response = await adminUpdateProduct(productId, cleanUpdateData);
      if (response.data.success) {
        saveToHistory(entry);
        await fetchProducts();
        alert('Stock updated successfully!');
        setEditingStock(null);
        setAdjustmentReason('');
        setNewStockValue('');
      }
    } catch (error) {
      console.error('Failed to update stock:', error);
      alert('Failed to update stock. Please try again.');
    }
  };

  const handleReturn = async () => {
    if (!returnItem || returnQuantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    if (!returnReason) {
      alert('Please provide a reason for the return');
      return;
    }

    const product = products.find(p => p._id === returnItem.productId);
    if (!product) return;

    let oldQuantity = 0;
    let updateData: any = {};
    let identifier = '';

    if (returnItem.type === 'base') {
      oldQuantity = product.stockQuantity || 0;
      if (returnQuality === 'good') {
        // ✅ GOOD RETURN: Restock the item
        updateData = { 
          stockQuantity: oldQuantity + returnQuantity,
          returnedToStock: (product.returnedToStock || 0) + returnQuantity,
          inStock: true
        };
      } else {
        // ✅ BAD RETURN: Move to damaged (DO NOT reduce stockQuantity)
        // The item is already removed from stock (it was sold), so we just track it as damaged
        updateData = { 
          damagedQuantity: (product.damagedQuantity || 0) + returnQuantity
        };
        // stockQuantity stays the same because the item was already deducted when sold
      }
      identifier = 'Base Stock';
    } else if (returnItem.type === 'variation' && returnItem.variationIndex !== undefined) {
      const variation = product.variations![returnItem.variationIndex];
      oldQuantity = variation.stockQuantity || 0;
      const updatedVariations = [...(product.variations || [])];
      if (returnQuality === 'good') {
        // ✅ GOOD RETURN: Restock the variation
        updatedVariations[returnItem.variationIndex] = { 
          ...variation, 
          stockQuantity: oldQuantity + returnQuantity 
        };
      }
      // For bad returns, we don't modify stockQuantity (item already deducted when sold)
      // We'll track it in damagedQuantity separately
      updateData = { variations: updatedVariations };
      if (returnQuality === 'bad') {
        // Also update damaged quantity at the product level
        updateData.damagedQuantity = (product.damagedQuantity || 0) + returnQuantity;
      }
      identifier = `Color: ${variation.color}`;
    } else if (returnItem.type === 'size' && returnItem.sizeIndex !== undefined) {
      const size = product.ukShoeSizes![returnItem.sizeIndex];
      oldQuantity = size.stockQuantity;
      const updatedSizes = [...(product.ukShoeSizes || [])];
      if (returnQuality === 'good') {
        // ✅ GOOD RETURN: Restock the size
        updatedSizes[returnItem.sizeIndex] = { 
          ...size, 
          stockQuantity: oldQuantity + returnQuantity 
        };
      }
      updateData = { ukShoeSizes: updatedSizes };
      if (returnQuality === 'bad') {
        // Also update damaged quantity at the product level
        updateData.damagedQuantity = (product.damagedQuantity || 0) + returnQuantity;
      }
      identifier = `UK Size: ${size.ukSize}`;
    }

    // For good returns, update stock and history
    if (returnQuality === 'good') {
      await updateStock(
        product._id,
        updateData,
        {
          productId: product._id,
          productName: product.name,
          productIdentifier: product.productId,
          type: returnItem.type,
          identifier,
          oldQuantity,
          newQuantity: oldQuantity + returnQuantity,
          change: returnQuantity,
          reason: `Return (${returnQuality}): ${returnReason}`,
          returnQuality
        }
      );
    } else {
      // For bad returns, update damaged count and history
      try {
        await adminUpdateProduct(product._id, updateData);
        saveToHistory({
          productId: product._id,
          productName: product.name,
          productIdentifier: product.productId,
          type: returnItem.type,
          identifier,
          oldQuantity,
          newQuantity: oldQuantity, // stockQuantity doesn't change
          change: 0, // No change to stock quantity
          reason: `Damaged return: ${returnReason}`,
          returnQuality: 'bad'
        });
        await fetchProducts();
      } catch (error) {
        console.error('Failed to process damaged return:', error);
        alert('Failed to process return');
        return;
      }
    }

    // Save return record
    saveReturn({
      id: Date.now().toString(),
      productId: product._id,
      productName: product.name,
      productIdentifier: product.productId,
      quantity: returnQuantity,
      quality: returnQuality,
      reason: returnReason,
      date: new Date(),
      processedBy: localStorage.getItem('userName') || 'Admin'
    });

    alert(`Return processed: ${returnQuantity} item(s) marked as ${returnQuality}`);
    setShowReturnModal(false);
    setReturnItem(null);
    setReturnQuantity(1);
    setReturnReason('');
  };

  // In StockManager.tsx - Update handleStartQuantityUpdate
  const handleStartQuantityUpdate = async () => {
    if (!startQuantityProduct) return;
    
    const newStartQuantity = parseInt(startQuantityValue);
    if (isNaN(newStartQuantity) || newStartQuantity < 0) {
      alert('Please enter a valid start quantity');
      return;
    }

    const oldStartQuantity = startQuantityProduct.startQuantity || 0;
    
    try {
      // ✅ Use the dedicated baseline endpoint
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/stock/set-baseline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          productId: startQuantityProduct._id,
          startQuantity: newStartQuantity
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        saveToHistory({
          productId: startQuantityProduct._id,
          productName: startQuantityProduct.name,
          productIdentifier: startQuantityProduct.productId,
          type: 'base',
          identifier: 'Start Quantity',
          oldQuantity: oldStartQuantity,
          newQuantity: newStartQuantity,
          change: newStartQuantity - oldStartQuantity,
          reason: 'Initial stock setup / Baseline stock adjustment'
        });
        
        await fetchProducts();
        alert(`Start quantity set to ${newStartQuantity}`);
        setShowStartQuantityModal(false);
        setStartQuantityProduct(null);
        setStartQuantityValue('');
      } else {
        alert(data.error || 'Failed to update start quantity');
      }
    } catch (error) {
      console.error('Failed to update start quantity:', error);
      alert('Failed to update start quantity');
    }
  };
  
  const handleSizeStockUpdate = (product: Product, sizeIndex: number, size: UKShoeSize) => {
    if (!adjustmentReason) {
      alert('Please provide a reason for stock adjustment');
      return;
    }
    
    const oldQuantity = size.stockQuantity;
    const newQuantity = parseInt(newStockValue);
    
    if (isNaN(newQuantity) || newQuantity < 0) {
      alert('Please enter a valid stock quantity');
      return;
    }
    
    const change = newQuantity - oldQuantity;
    const updatedSizes = [...(product.ukShoeSizes || [])];
    updatedSizes[sizeIndex] = { ...size, stockQuantity: newQuantity };
    
    updateStock(
      product._id,
      { ukShoeSizes: updatedSizes },
      {
        productId: product._id,
        productName: product.name,
        productIdentifier: product.productId,
        type: 'size',
        identifier: `UK Size: ${size.ukSize} (${size.footLengthCm}cm)`,
        oldQuantity,
        newQuantity,
        change,
        reason: adjustmentReason
      }
    );
  };

  const getLowStockProducts = () => {
    const lowStock: { product: Product; type: string; identifier: string; stock: number }[] = [];

    products.forEach(product => {
      if (product.stockQuantity > 0 && product.stockQuantity < 10) {
        lowStock.push({
          product,
          type: 'Base Stock',
          identifier: product.productId,
          stock: product.stockQuantity
        });
      }
      
      product.variations?.forEach(variation => {
        // ✅ Check if variation has sizes
        if (variation.sizes && variation.sizes.length > 0) {
          variation.sizes.forEach(size => {
            if (size.stockQuantity > 0 && size.stockQuantity < 10) {
              lowStock.push({
                product,
                type: 'Shoe Size',
                identifier: `${product.productId} - ${variation.color} - UK ${size.ukSize}`,
                stock: size.stockQuantity
              });
            }
          });
        } else if ((variation.stockQuantity || 0) > 0 && (variation.stockQuantity || 0) < 10) {
          lowStock.push({
            product,
            type: 'Color Variation',
            identifier: `${product.productId} - ${variation.color}`,
            stock: variation.stockQuantity || 0
          });
        }
      });
      
      product.ukShoeSizes?.forEach(size => {
        if (size.stockQuantity > 0 && size.stockQuantity < 10) {
          lowStock.push({
            product,
            type: 'Shoe Size',
            identifier: `${product.productId} - UK ${size.ukSize}`,
            stock: size.stockQuantity
          });
        }
      });
    });

    return lowStock;
  };

  const getStockSummary = () => {
    let totalValue = 0;
    let totalCost = 0;
    let totalStock = 0;
    let totalStartStock = 0;
    let totalAcquired = 0;
    let totalSold = 0;
    let totalDamaged = 0;
    let totalReturned = 0;
    
    products.forEach(product => {
      let productStock = 0;
      let productStartStock = product.startQuantity || 0;
      let productDamaged = product.damagedQuantity || 0;
      let productReturned = product.returnedToStock || 0;
      let productAcquired = product.totalAcquired || product.startQuantity || 0;
      
      if (product.variations && product.variations.length > 0) {
        // ✅ Check if variations have sizes
        productStock = product.variations.reduce((sum, v) => {
          if (v.sizes && v.sizes.length > 0) {
            // Sum stock from sizes
            return sum + v.sizes.reduce((sizeSum, s) => sizeSum + (s.stockQuantity || 0), 0);
          }
          // Fallback to variation stockQuantity
          return sum + (v.stockQuantity || 0);
        }, 0);
      } else if (product.ukShoeSizes && product.ukShoeSizes.length > 0) {
        productStock = product.ukShoeSizes.reduce((sum, s) => sum + (s.stockQuantity || 0), 0);
      } else {
        productStock = product.stockQuantity || 0;
      }
      
      totalStock += productStock;
      totalStartStock += productStartStock;
      totalAcquired += productAcquired;
      totalDamaged += productDamaged;
      totalReturned += productReturned;
      
      // ✅ CORRECT: Units Sold = totalAcquired - currentStock - damaged - returned
      const actualSold = Math.max(0, productAcquired - productStock - productDamaged - productReturned);
      totalSold += actualSold;
      
      totalValue += productStock * (product.price || 0);
      totalCost += productStock * (product.costPrice || 0);
    });
    
    return { 
      totalStock, 
      totalValue, 
      totalCost, 
      potentialProfit: totalValue - totalCost,
      totalStartStock,
      totalAcquired,
      totalSold,
      totalDamaged,
      totalReturned
    };
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.productId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...new Set(products.map(p => p.category))];
  const lowStockItems = getLowStockProducts();
  const stockSummary = getStockSummary();

  if (loading) {
    return <div className="stock-manager-loading">Loading stock data...</div>;
  }

  return (
    <div className="stock-manager">
      <div className="stock-manager-header">
        <h2>📦 Stock Manager</h2>
        <div className="stock-stats">
          <div className="stat-card total-products">
            <span className="stat-value">{products.length}</span>
            <span className="stat-label">Total Products</span>
          </div>
          <div className="stat-card total-stock">
            <span className="stat-value">{stockSummary.totalStock}</span>
            <span className="stat-label">Total Units</span>
          </div>
          <div className="stat-card stock-value">
            <span className="stat-value">₦{stockSummary.totalValue.toLocaleString()}</span>
            <span className="stat-label">Inventory Value</span>
          </div>
          <div className="stat-card potential-profit">
            <span className="stat-value">₦{stockSummary.potentialProfit.toLocaleString()}</span>
            <span className="stat-label">Potential Profit</span>
          </div>
          <div className="stat-card low-stock">
            <span className="stat-value">{lowStockItems.length}</span>
            <span className="stat-label">Low Stock Items</span>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      {lowStockItems.length > 0 && (
        <div className="stock-alerts">
          <h3>⚠️ Stock Alerts</h3>
          <div className="alert-section low-stock">
            <h4>🟡 Low Stock Items (&lt; 10 units)</h4>
            <ul>
              {lowStockItems.slice(0, 5).map((item, idx) => (
                <li key={idx}>
                  <span>{item.product.name}</span> - <span className="product-id-badge">{item.identifier}</span>
                  <span className="stock-count"> ({item.stock} left)</span>
                </li>
              ))}
              {lowStockItems.length > 5 && (
                <li className="more-items">+{lowStockItems.length - 5} more low stock items</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="stock-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, category, or product ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="category-filter">
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
            ))}
          </select>
        </div>
        <button className="history-btn" onClick={() => setShowHistory(!showHistory)}>
          📜 {showHistory ? 'Hide History' : 'View Stock History'}
        </button>
        <button className="returns-btn" onClick={() => setShowReturns(!showReturns)}>
          🔄 {showReturns ? 'Hide Returns' : 'View Returns Log'}
        </button>
        <button className="refresh-btn" onClick={fetchProducts}>🔄 Refresh</button>
      </div>

      {/* Stock History Modal */}
      {showHistory && (
        <div className="stock-history-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📜 Stock Adjustment History</h3>
              <button className="close-btn" onClick={() => setShowHistory(false)}>×</button>
            </div>
            <div className="modal-body">
              {stockHistory.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <p>No stock adjustments recorded yet.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="history-table-container desktop-view">
                    <table className="history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product ID</th>
                          <th>Product</th>
                          <th>Item</th>
                          <th>Old Stock</th>
                          <th>New Stock</th>
                          <th>Change</th>
                          <th>Reason</th>
                          <th>Admin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockHistory.map((entry, idx) => (
                          <tr key={idx} className={entry.change < 0 ? 'negative' : 'positive'}>
                            <td>{new Date(entry.date).toLocaleString()}</td>
                            <td className="product-id-cell">{entry.productIdentifier}</td>
                            <td>{entry.productName}</td>
                            <td>{entry.identifier}</td>
                            <td>{entry.oldQuantity}</td>
                            <td>{entry.newQuantity}</td>
                            <td className={entry.change > 0 ? 'change-positive' : 'change-negative'}>
                              {entry.change > 0 ? `+${entry.change}` : entry.change}
                            </td>
                            <td>{entry.reason}{entry.returnQuality && ` (${entry.returnQuality})`}</td>
                            <td>{entry.adminName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="history-cards mobile-view">
                    {stockHistory.map((entry, idx) => (
                      <div key={idx} className={`history-card ${entry.change < 0 ? 'negative' : 'positive'}`}>
                        <div className="history-card-header">
                          <span className="history-card-date">{new Date(entry.date).toLocaleString()}</span>
                          <span className={`history-card-change ${entry.change > 0 ? 'positive' : 'negative'}`}>
                            {entry.change > 0 ? `+${entry.change}` : entry.change}
                          </span>
                        </div>
                        <div className="history-card-body">
                          <div className="history-card-row">
                            <span className="history-card-label">Product:</span>
                            <span className="history-card-value">{entry.productName}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Item:</span>
                            <span className="history-card-value">{entry.identifier}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Stock:</span>
                            <span className="history-card-value">
                              {entry.oldQuantity} → {entry.newQuantity}
                            </span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Reason:</span>
                            <span className="history-card-value">{entry.reason}{entry.returnQuality && ` (${entry.returnQuality})`}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Admin:</span>
                            <span className="history-card-value">{entry.adminName}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Product ID:</span>
                            <span className="history-card-value product-id-cell">{entry.productIdentifier}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Returns Log Modal - Mobile Friendly */}
      {showReturns && (
        <div className="stock-history-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>🔄 Returns Log</h3>
              <button className="close-btn" onClick={() => setShowReturns(false)}>×</button>
            </div>
            <div className="modal-body">
              {returns.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <p>No returns recorded yet.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="history-table-container desktop-view">
                    <table className="history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product ID</th>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Quality</th>
                          <th>Reason</th>
                          <th>Processed By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returns.map((ret, idx) => (
                          <tr key={idx} className={ret.quality === 'bad' ? 'negative' : 'positive'}>
                            <td>{new Date(ret.date).toLocaleString()}</td>
                            <td className="product-id-cell">{ret.productIdentifier}</td>
                            <td>{ret.productName}</td>
                            <td>{ret.quantity}</td>
                            <td className={ret.quality === 'good' ? 'quality-good' : 'quality-bad'}>
                              {ret.quality === 'good' ? '✅ Good' : '❌ Bad'}
                            </td>
                            <td>{ret.reason}</td>
                            <td>{ret.processedBy}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="history-cards mobile-view">
                    {returns.map((ret, idx) => (
                      <div key={idx} className={`history-card ${ret.quality === 'bad' ? 'negative' : 'positive'}`}>
                        <div className="history-card-header">
                          <span className="history-card-date">{new Date(ret.date).toLocaleString()}</span>
                          <span className={`history-card-quality ${ret.quality === 'good' ? 'good' : 'bad'}`}>
                            {ret.quality === 'good' ? '✅ Good' : '❌ Bad'}
                          </span>
                        </div>
                        <div className="history-card-body">
                          <div className="history-card-row">
                            <span className="history-card-label">Product:</span>
                            <span className="history-card-value">{ret.productName}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Quantity:</span>
                            <span className="history-card-value">{ret.quantity}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Reason:</span>
                            <span className="history-card-value">{ret.reason}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Processed By:</span>
                            <span className="history-card-value">{ret.processedBy}</span>
                          </div>
                          <div className="history-card-row">
                            <span className="history-card-label">Product ID:</span>
                            <span className="history-card-value product-id-cell">{ret.productIdentifier}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="products-stock-table">
        <table>
          <thead>
            <tr>
              <th>Product ID</th>
              <th>Product</th>
              <th>Category</th>
              <th>Start Qty</th>
              <th>All Qty</th>
              <th>Curr Qty</th>
              <th>Sold</th>
              <th>Damaged</th>
              <th>Returned</th>
              <th>Variations</th>
              <th>Sizes</th>
              <th>Total Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => {
              const totalStock = (() => {
                if (product.variations && product.variations.length > 0) {
                  return product.variations.reduce((sum, v) => {
                    if (v.sizes && v.sizes.length > 0) {
                      return sum + v.sizes.reduce((sizeSum, s) => sizeSum + s.stockQuantity, 0);
                    }
                    return sum + (v.stockQuantity || 0);
                  }, 0);
                }
                if (product.ukShoeSizes && product.ukShoeSizes.length > 0) {
                  return product.ukShoeSizes.reduce((sum, s) => sum + s.stockQuantity, 0);
                }
                return product.stockQuantity || 0;
              })();
              
              const isLowTotal = totalStock > 0 && totalStock < 10;
              
              // Check if product has variations with sizes
              const hasVariationsWithSizes = product.variations?.some(v => v.sizes && v.sizes.length > 0) || false;
              
              return (
                <tr key={product._id} className={isLowTotal ? 'low-stock-row' : ''}>
                  <td className="product-id-cell">
                    <span className="product-id-badge">{product.productId.slice(-8)}</span>
                    <button
                      className="copy-id-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(product.productId);
                        alert('Product ID copied!');
                      }}
                      title="Copy full ID"
                    >
                      📋
                    </button>
                  </td>
                  <td>
                    <div className="product-info">
                      <img src={getFullImageUrl(product.image)} alt={product.name} className="product-thumb" />
                      <span className="product-name">{product.name}</span>
                    </div>
                  </td>
                  <td><span className="category-badge">{product.category}</span></td>
                  
                  {/* Start Qty - Nested for variations with sizes */}
                  <td className="stock-cell">
                    {hasVariationsWithSizes ? (
                      <div className="nested-stock-cells">
                        {product.variations.map((v, idx) => {
                          if (v.sizes && v.sizes.length > 0) {
                            return v.sizes.map((size, sizeIdx) => (
                              <div key={`start-${idx}-${sizeIdx}`} className="nested-stock-item">
                                <span className="size-label">UK {size.ukSize}</span>
                                <span className="nested-stock-value">{size.startQuantity || 0}</span>
                              </div>
                            ));
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <span>{product.startQuantity || product.stockQuantity || 0}</span>
                    )}
                  </td>
                  
                  {/* All Time Qty - Nested for variations with sizes */}
                  <td className="stock-cell">
                    {hasVariationsWithSizes ? (
                      <div className="nested-stock-cells">
                        {product.variations.map((v, idx) => {
                          if (v.sizes && v.sizes.length > 0) {
                            return v.sizes.map((size, sizeIdx) => (
                              <div key={`alltime-${idx}-${sizeIdx}`} className="nested-stock-item">
                                <span className="size-label">UK {size.ukSize}</span>
                                <span className="nested-stock-value">{size.totalAcquired || 0}</span>
                              </div>
                            ));
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <span className="stock-value acquired">
                        {product.totalAcquired || product.startQuantity || product.stockQuantity || 0}
                      </span>
                    )}
                  </td>
                  
                  {/* Current Stock - Show each size individually with edit button */}
                  <td className="stock-cell">
                    {(() => {
                      // For products with variations and sizes
                      if (hasVariationsWithSizes) {
                        return (
                          <div className="nested-stock-cells">
                            {product.variations.map((v, vIdx) => {
                              if (v.sizes && v.sizes.length > 0) {
                                return v.sizes.map((size, sIdx) => (
                                  <div key={`current-${vIdx}-${sIdx}`} className="nested-stock-item">
                                    <span className="size-label">UK {size.ukSize}</span>
                                    <span className={`nested-stock-value ${size.stockQuantity === 0 ? 'out' : size.stockQuantity < 10 ? 'low' : 'in'}`}>
                                      {size.stockQuantity}
                                    </span>
                                    {/* ✅ Edit button for each individual size */}
                                    <button
                                      className="edit-stock-btn small"
                                      onClick={() => {
                                        setEditingStock({
                                          productId: product._id,
                                          type: 'base',  // ← Use 'base' type
                                          currentStock: size.stockQuantity,
                                          // Store which specific size to update
                                          variationIndex: vIdx,
                                          sizeIndex: sIdx
                                        });
                                        setAdjustmentAmount('');
                                        setNewStockValue(String(size.stockQuantity));
                                      }}
                                    >
                                      ✏️
                                    </button>
                                  </div>
                                ));
                              }
                              return null;
                            })}
                          </div>
                        );
                      } else if (product.variations && product.variations.length > 0) {
                        // Color-only products - show each color
                        return (
                          <div className="nested-stock-cells">
                            {product.variations.map((v, idx) => (
                              <div key={`current-${idx}`} className="nested-stock-item">
                                <span className="size-label">
                                  <span className="color-dot" style={{ backgroundColor: v.colorCode }} />
                                  {v.color}
                                </span>
                                <span className={`nested-stock-value ${(v.stockQuantity || 0) === 0 ? 'out' : (v.stockQuantity || 0) < 10 ? 'low' : 'in'}`}>
                                  {v.stockQuantity || 0}
                                </span>
                                <button
                                  className="edit-stock-btn small"
                                  onClick={() => {
                                    setEditingStock({
                                      productId: product._id,
                                      type: 'base',
                                      currentStock: v.stockQuantity || 0,
                                      variationIndex: idx
                                    });
                                    setAdjustmentAmount('');
                                    setNewStockValue(String(v.stockQuantity || 0));
                                  }}
                                >
                                  ✏️
                                </button>
                              </div>
                            ))}
                          </div>
                        );
                      } else {
                        // Simple product - single stock number
                        return (
                          <div className="stock-display-wrapper">
                            <span className={`stock-value ${product.stockQuantity === 0 ? 'out' : product.stockQuantity < 10 ? 'low' : 'in'}`}>
                              {product.stockQuantity}
                            </span>
                            <button
                              className="edit-stock-btn"
                              onClick={() => {
                                setEditingStock({
                                  productId: product._id,
                                  type: 'base',
                                  currentStock: product.stockQuantity || 0
                                });
                                setAdjustmentAmount('');
                                setNewStockValue(String(product.stockQuantity || 0));
                              }}
                            >
                              ✏️
                            </button>
                          </div>
                        );
                      }
                    })()}
                  </td>
                  
                  {/* Units Sold - Nested for variations with sizes */}
                  <td className="stock-cell">
                    {hasVariationsWithSizes ? (
                      <div className="nested-stock-cells">
                        {product.variations.map((v, idx) => {
                          if (v.sizes && v.sizes.length > 0) {
                            return v.sizes.map((size, sizeIdx) => (
                              <div key={`sold-${idx}-${sizeIdx}`} className="nested-stock-item">
                                <span className="size-label">UK {size.ukSize}</span>
                                <span className="nested-stock-value sold">
                                  {Math.max(0, size.totalAcquired - size.stockQuantity - size.damagedQuantity - size.returnedToStock)}
                                </span>
                              </div>
                            ));
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <span className="stock-value sold">
                        {Math.max(0, (product.totalAcquired || product.startQuantity || 0) - (product.stockQuantity || 0) - (product.damagedQuantity || 0) - (product.returnedToStock || 0))}
                      </span>
                    )}
                  </td>
                  
                  {/* Damaged - Nested for variations with sizes */}
                  <td className="stock-cell">
                    {hasVariationsWithSizes ? (
                      <div className="nested-stock-cells">
                        {product.variations.map((v, idx) => {
                          if (v.sizes && v.sizes.length > 0) {
                            return v.sizes.map((size, sizeIdx) => (
                              <div key={`damaged-${idx}-${sizeIdx}`} className="nested-stock-item">
                                <span className="size-label">UK {size.ukSize}</span>
                                <span className={`nested-stock-value ${size.damagedQuantity === 0 ? 'in' : 'damaged'}`}>
                                  {size.damagedQuantity || 0}
                                </span>
                              </div>
                            ));
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <span className={`stock-value ${product.damagedQuantity === 0 ? 'in' : 'damaged'}`}>
                        {product.damagedQuantity || 0}
                      </span>
                    )}
                  </td>
                  
                  {/* Returned - Nested for variations with sizes */}
                  <td className="stock-cell">
                    {hasVariationsWithSizes ? (
                      <div className="nested-stock-cells">
                        {product.variations.map((v, idx) => {
                          if (v.sizes && v.sizes.length > 0) {
                            return v.sizes.map((size, sizeIdx) => (
                              <div key={`returned-${idx}-${sizeIdx}`} className="nested-stock-item">
                                <span className="size-label">UK {size.ukSize}</span>
                                <span className="nested-stock-value returned">
                                  {size.returnedToStock || 0}
                                </span>
                              </div>
                            ));
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <span className="stock-value returned">
                        {product.returnedToStock || 0}
                      </span>
                    )}
                  </td>
                  
                  {/* Variations Column - READ ONLY now */}
                  <td>
                    {product.variations && product.variations.length > 0 ? (
                      <div className="variations-stock">
                        {product.variations.map((v, idx) => {
                          if (v.sizes && v.sizes.length > 0) {
                            return v.sizes.map((size, sizeIdx) => (
                              <div key={`${idx}-${sizeIdx}`} className="variation-stock-item">
                                <span 
                                  className="color-dot" 
                                  style={{ backgroundColor: v.colorCode }}
                                  title={v.color}
                                />
                                <span className="size-label">UK {size.ukSize}</span>
                                <span className="stock-badge">{size.stockQuantity}</span>
                              </div>
                            ));
                          } else {
                            // Color-only variation - READ ONLY
                            return (
                              <div key={idx} className="variation-stock-item">
                                <span 
                                  className="color-dot" 
                                  style={{ backgroundColor: v.colorCode }}
                                  title={v.color}
                                />
                                <span className="stock-badge">{v.stockQuantity || 0}</span>
                              </div>
                            );
                          }
                        })}
                      </div>
                    ) : '-'}
                  </td>
                  
                  {/* Sizes Column */}
                  <td>
                    {product.category === 'shoes' && product.ukShoeSizes && product.ukShoeSizes.length > 0 ? (
                      <div className="sizes-stock">
                        {product.ukShoeSizes.map((size, idx) => (
                          <div key={idx} className="size-stock-item">
                            <span className="size-label">UK {size.ukSize}</span>
                            <button
                              className="edit-stock-btn small"
                              onClick={() => {
                                setEditingStock({
                                  productId: product._id,
                                  type: 'size',
                                  sizeIndex: idx,
                                  currentStock: size.stockQuantity
                                });
                                setNewStockValue(String(size.stockQuantity));
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              className="return-stock-btn small"
                              onClick={() => {
                                setReturnItem({
                                  productId: product._id,
                                  productName: product.name,
                                  productIdentifier: product.productId,
                                  type: 'size',
                                  sizeIndex: idx,
                                  currentStock: size.stockQuantity
                                });
                                setShowReturnModal(true);
                              }}
                              title="Process return"
                            >
                              🔄
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : '-'}
                  </td>
                  
                  <td className={`total-stock ${totalStock === 0 ? 'out' : totalStock < 10 ? 'low' : ''}`}>
                    {totalStock}
                  </td>
                  <td>
                    <button 
                      className="view-details-btn"
                      onClick={() => window.open(`/product/${product._id}`, '_blank')}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Stock Modal */}
      {editingStock && (
        <div className="stock-edit-modal-overlay" onClick={() => setEditingStock(null)}>
          <div className="stock-edit-modal" onClick={e => e.stopPropagation()}>
            <h3>Adjust Stock</h3>
            
            <div className="form-group">
              <label>Current Stock:</label>
              <input type="number" value={editingStock.currentStock} disabled />
            </div>
            
            <div className="form-group">
              <label>Adjustment Amount:</label>
              <input
                type="number"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                autoFocus
                placeholder="Enter amount to add (+) or remove (-)"
              />
              <small className="adjustment-hint">
                Enter positive number to add stock, negative to remove stock.
                {adjustmentAmount && !isNaN(parseInt(adjustmentAmount)) && (
                  <span className={`preview-result ${parseInt(adjustmentAmount) < 0 ? 'negative' : ''}`}>
                    New stock will be: {editingStock.currentStock + parseInt(adjustmentAmount)}
                    {parseInt(adjustmentAmount) > 0 && ` (+${adjustmentAmount})`}
                    {parseInt(adjustmentAmount) < 0 && ` (${adjustmentAmount})`}
                  </span>
                )}
              </small>
            </div>
            
            <div className="form-group">
              <label>Reason for Adjustment:</label>
              <select value={adjustmentReason} onChange={(e) => setAdjustmentReason(e.target.value)}>
                <option value="">Select reason...</option>
                <option value="New shipment received">📦 New shipment received (adds stock)</option>
                <option value="Order fulfillment">🛒 Order fulfillment (removes stock)</option>
                <option value="Return processed (good)">🔄 Return processed (good) - restocks</option>
                <option value="Return processed (damaged) - Move to damaged">⚠️ Return processed (damaged) - track loss</option>
                <option value="Damaged items (write-off) - Move to damaged">⚠️ Damaged items write-off (remove stock, track loss)</option>
                <option value="Stock take adjustment">📊 Stock taking</option>
                <option value="Stock correction">✅ Stock correction</option>
                <option value="Other">📝 Other</option>
              </select>
            </div>
            
            <div className="modal-actions">
              <button className="save-btn" onClick={() => {
                const product = products.find(p => p._id === editingStock.productId);
                if (!product) return;
                
                const adjustment = parseInt(adjustmentAmount);
                if (isNaN(adjustment) || adjustment === 0) {
                  alert('Please enter a valid adjustment amount');
                  return;
                }
                
                const newTotalStock = (editingStock.currentStock || 0) + adjustment;
                
                setNewStockValue(String(newTotalStock));
                
                // BASE STOCK UPDATE
                if (editingStock.type === 'base') {
                  const product = products.find(p => p._id === editingStock.productId);
                  if (!product) return;
                  
                  // Check if we're updating a specific variation/size
                  const hasVariationIndex = editingStock.variationIndex !== undefined && editingStock.variationIndex !== null;
                  const hasSizeIndex = editingStock.sizeIndex !== undefined && editingStock.sizeIndex !== null;
                  
                  // 🔴 DAMAGED WRITE-OFF
                  if (adjustmentReason === 'Damaged items (write-off) - Move to damaged' && adjustment < 0) {
                    const damagedIncrease = Math.abs(adjustment);
                    
                    // If updating a specific variation/size
                    if (hasVariationIndex && product.variations && product.variations.length > editingStock.variationIndex!) {
                      const variation = product.variations[editingStock.variationIndex!];
                      
                      if (hasSizeIndex && variation.sizes && variation.sizes.length > editingStock.sizeIndex!) {
                        // Update specific size
                        const size = variation.sizes[editingStock.sizeIndex!];
                        const updatedSizes = [...variation.sizes];
                        updatedSizes[editingStock.sizeIndex!] = {
                          ...size,
                          stockQuantity: Math.max(0, size.stockQuantity + adjustment)
                        };
                        
                        const updatedVariations = [...(product.variations || [])];
                        updatedVariations[editingStock.variationIndex!] = {
                          ...variation,
                          sizes: updatedSizes
                        };
                        
                        updateStock(
                          product._id,
                          { 
                            variations: updatedVariations,
                            damagedQuantity: (product.damagedQuantity || 0) + damagedIncrease
                          },
                          {
                            productId: product._id,
                            productName: product.name,
                            productIdentifier: product.productId,
                            type: 'base',
                            identifier: `Color: ${variation.color} - UK ${size.ukSize}`,
                            oldQuantity: editingStock.currentStock,
                            newQuantity: editingStock.currentStock + adjustment,
                            change: adjustment,
                            reason: adjustmentReason
                          }
                        );
                      } else {
                        // Update entire variation (color-only)
                        const updatedVariations = [...(product.variations || [])];
                        updatedVariations[editingStock.variationIndex!] = {
                          ...variation,
                          stockQuantity: Math.max(0, (variation.stockQuantity || 0) + adjustment)
                        };
                        
                        updateStock(
                          product._id,
                          { 
                            variations: updatedVariations,
                            damagedQuantity: (product.damagedQuantity || 0) + damagedIncrease
                          },
                          {
                            productId: product._id,
                            productName: product.name,
                            productIdentifier: product.productId,
                            type: 'base',
                            identifier: `Color: ${variation.color}`,
                            oldQuantity: editingStock.currentStock,
                            newQuantity: editingStock.currentStock + adjustment,
                            change: adjustment,
                            reason: adjustmentReason
                          }
                        );
                      }
                    } else {
                      // Simple product
                      const updateData = { 
                        stockQuantity: newTotalStock, 
                        inStock: newTotalStock > 0,
                        damagedQuantity: (product.damagedQuantity || 0) + damagedIncrease
                      };
                      
                      updateStock(
                        product._id,
                        updateData,
                        {
                          productId: product._id,
                          productName: product.name,
                          productIdentifier: product.productId,
                          type: 'base',
                          identifier: 'Base Stock',
                          oldQuantity: editingStock.currentStock,
                          newQuantity: newTotalStock,
                          change: adjustment,
                          reason: adjustmentReason
                        }
                      );
                    }
                    return;
                  }
                  
                  // ✅ NORMAL BASE STOCK ADJUSTMENT - Handles ALL product types
                  if (hasVariationIndex && product.variations && product.variations.length > editingStock.variationIndex!) {
                    const variation = product.variations[editingStock.variationIndex!];
                    
                    if (hasSizeIndex && variation.sizes && variation.sizes.length > editingStock.sizeIndex!) {
                      // Update specific size
                      const size = variation.sizes[editingStock.sizeIndex!];
                      const sizeNewStock = Math.max(0, size.stockQuantity + adjustment);
                      const updatedSizes = [...variation.sizes];
                      updatedSizes[editingStock.sizeIndex!] = {
                        ...size,
                        stockQuantity: sizeNewStock
                      };
                      
                      const updatedVariations = [...(product.variations || [])];
                      updatedVariations[editingStock.variationIndex!] = {
                        ...variation,
                        sizes: updatedSizes
                      };
                      
                      const updateData: any = { variations: updatedVariations };
                      
                      // If this is a new shipment, update totalAcquired
                      if (adjustmentReason === 'New shipment received' && adjustment > 0) {
                        // Also update totalAcquired for this size
                        const updatedSizesWithTotal = [...variation.sizes];
                        updatedSizesWithTotal[editingStock.sizeIndex!] = {
                          ...size,
                          stockQuantity: sizeNewStock,
                          totalAcquired: (size.totalAcquired || 0) + adjustment
                        };
                        updatedVariations[editingStock.variationIndex!] = {
                          ...variation,
                          sizes: updatedSizesWithTotal
                        };
                        updateData.variations = updatedVariations;
                        updateData.totalAcquired = (product.totalAcquired || 0) + adjustment;
                      }
                      
                      updateStock(
                        product._id,
                        updateData,
                        {
                          productId: product._id,
                          productName: product.name,
                          productIdentifier: product.productId,
                          type: 'base',
                          identifier: `Color: ${variation.color} - UK ${size.ukSize}`,
                          oldQuantity: editingStock.currentStock,
                          newQuantity: sizeNewStock,
                          change: adjustment,
                          reason: adjustmentReason
                        }
                      );
                    } else {
                      // Update entire variation (color-only)
                      const varNewStock = Math.max(0, (variation.stockQuantity || 0) + adjustment);
                      const updatedVariations = [...(product.variations || [])];
                      const updateData: any = { 
                        variations: updatedVariations
                      };
                      
                      updatedVariations[editingStock.variationIndex!] = {
                        ...variation,
                        stockQuantity: varNewStock
                      };
                      
                      if (adjustmentReason === 'New shipment received' && adjustment > 0) {
                        updatedVariations[editingStock.variationIndex!] = {
                          ...variation,
                          stockQuantity: varNewStock,
                          totalAcquired: (variation.totalAcquired || 0) + adjustment
                        };
                        updateData.totalAcquired = (product.totalAcquired || 0) + adjustment;
                      }
                      
                      updateStock(
                        product._id,
                        updateData,
                        {
                          productId: product._id,
                          productName: product.name,
                          productIdentifier: product.productId,
                          type: 'base',
                          identifier: `Color: ${variation.color}`,
                          oldQuantity: editingStock.currentStock,
                          newQuantity: varNewStock,
                          change: adjustment,
                          reason: adjustmentReason
                        }
                      );
                    }
                  } else {
                    // Simple product
                    const updateData: any = { 
                      stockQuantity: newTotalStock, 
                      inStock: newTotalStock > 0 
                    };
                    
                    if (adjustmentReason === 'New shipment received' && adjustment > 0) {
                      updateData.totalAcquired = (product.totalAcquired || 0) + adjustment;
                    }
                    
                    updateStock(
                      product._id,
                      updateData,
                      {
                        productId: product._id,
                        productName: product.name,
                        productIdentifier: product.productId,
                        type: 'base',
                        identifier: 'Base Stock',
                        oldQuantity: editingStock.currentStock,
                        newQuantity: newTotalStock,
                        change: adjustment,
                        reason: adjustmentReason
                      }
                    );
                  }
                  return;
                  }

                
                // ============================================
                // VARIATION STOCK UPDATE (Color-only)
                // ============================================
                if (editingStock.type === 'variation' && editingStock.variationIndex !== undefined) {
                  const variation = product.variations![editingStock.variationIndex];
                  
                  // ✅ If there's a sizeIndex, update the specific size
                  if (editingStock.sizeIndex !== undefined && variation.sizes && variation.sizes.length > editingStock.sizeIndex) {
                    // Update specific size within the variation
                    const size = variation.sizes[editingStock.sizeIndex];
                    const sizeOldQuantity = size.stockQuantity;
                    const sizeNewTotal = sizeOldQuantity + adjustment;
                    
                    // 🔴 DAMAGED WRITE-OFF FOR SPECIFIC SIZE
                    if (adjustmentReason === 'Damaged items (write-off) - Move to damaged' && adjustment < 0) {
                      const damagedIncrease = Math.abs(adjustment);
                      const updatedSizes = [...variation.sizes];
                      updatedSizes[editingStock.sizeIndex] = { 
                        ...size, 
                        stockQuantity: sizeNewTotal 
                      };
                      
                      const updatedVariations = [...(product.variations || [])];
                      updatedVariations[editingStock.variationIndex] = { 
                        ...variation, 
                        sizes: updatedSizes 
                      };
                      
                      updateStock(
                        product._id,
                        { 
                          variations: updatedVariations,
                          damagedQuantity: (product.damagedQuantity || 0) + damagedIncrease
                        },
                        {
                          productId: product._id,
                          productName: product.name,
                          productIdentifier: product.productId,
                          type: 'size',
                          identifier: `Color: ${variation.color} - UK ${size.ukSize}`,
                          oldQuantity: sizeOldQuantity,
                          newQuantity: sizeNewTotal,
                          change: adjustment,
                          reason: adjustmentReason
                        }
                      );
                      return;
                    }
                    
                    // ✅ NORMAL SIZE UPDATE
                    const updatedSizes = [...variation.sizes];
                    updatedSizes[editingStock.sizeIndex] = { 
                      ...size, 
                      stockQuantity: sizeNewTotal 
                    };
                    
                    const updatedVariations = [...(product.variations || [])];
                    updatedVariations[editingStock.variationIndex] = { 
                      ...variation, 
                      sizes: updatedSizes 
                    };
                    
                    // If this is a new shipment for this size
                    if (adjustmentReason === 'New shipment received' && adjustment > 0) {
                      // Update totalAcquired for the specific size
                      const updatedSizeWithTotal = { 
                        ...size, 
                        stockQuantity: sizeNewTotal,
                        totalAcquired: (size.totalAcquired || 0) + adjustment
                      };
                      updatedSizes[editingStock.sizeIndex] = updatedSizeWithTotal;
                      updatedVariations[editingStock.variationIndex] = { 
                        ...variation, 
                        sizes: updatedSizes 
                      };
                    }
                    
                    updateStock(
                      product._id,
                      { variations: updatedVariations },
                      {
                        productId: product._id,
                        productName: product.name,
                        productIdentifier: product.productId,
                        type: 'size',
                        identifier: `Color: ${variation.color} - UK ${size.ukSize}`,
                        oldQuantity: sizeOldQuantity,
                        newQuantity: sizeNewTotal,
                        change: adjustment,
                        reason: adjustmentReason
                      }
                    );
                    return;
                  }
                  
                  // ❌ No sizeIndex - update the variation's stockQuantity (color-only product)
                  const varOldQuantity = variation.stockQuantity || 0;
                  const varNewTotal = varOldQuantity + adjustment;
                  
                  // 🔴 DAMAGED WRITE-OFF FOR COLOR-ONLY VARIATION
                  if (adjustmentReason === 'Damaged items (write-off) - Move to damaged' && adjustment < 0) {
                    const damagedIncrease = Math.abs(adjustment);
                    const updatedVariations = [...(product.variations || [])];
                    updatedVariations[editingStock.variationIndex] = { 
                      ...variation, 
                      stockQuantity: varNewTotal 
                    };
                    
                    updateStock(
                      product._id,
                      { 
                        variations: updatedVariations,
                        damagedQuantity: (product.damagedQuantity || 0) + damagedIncrease
                      },
                      {
                        productId: product._id,
                        productName: product.name,
                        productIdentifier: product.productId,
                        type: 'variation',
                        identifier: `Color: ${variation.color}`,
                        oldQuantity: varOldQuantity,
                        newQuantity: varNewTotal,
                        change: adjustment,
                        reason: adjustmentReason
                      }
                    );
                    return;
                  }
                  
                  // ✅ NORMAL COLOR-ONLY VARIATION UPDATE
                  const updatedVariations = [...(product.variations || [])];
                  updatedVariations[editingStock.variationIndex] = { 
                    ...variation, 
                    stockQuantity: varNewTotal 
                  };
                  
                  // If this is a new shipment for this variation
                  if (adjustmentReason === 'New shipment received' && adjustment > 0) {
                    // Update totalAcquired for the variation
                    updatedVariations[editingStock.variationIndex] = { 
                      ...variation, 
                      stockQuantity: varNewTotal,
                      totalAcquired: (variation.totalAcquired || 0) + adjustment
                    };
                  }
                  
                  updateStock(
                    product._id,
                    { variations: updatedVariations },
                    {
                      productId: product._id,
                      productName: product.name,
                      productIdentifier: product.productId,
                      type: 'variation',
                      identifier: `Color: ${variation.color}`,
                      oldQuantity: varOldQuantity,
                      newQuantity: varNewTotal,
                      change: adjustment,
                      reason: adjustmentReason
                    }
                  );
                  return;
                }
                
                // ============================================
                // SIZE STOCK UPDATE (Direct ukShoeSizes)
                // ============================================
                if (editingStock.type === 'size' && editingStock.sizeIndex !== undefined) {
                  const size = product.ukShoeSizes![editingStock.sizeIndex];
                  if (size) {
                    handleSizeStockUpdate(product, editingStock.sizeIndex, size);
                  }
                  
                  const sizeOldQuantity = size.stockQuantity;
                  const sizeNewTotal = sizeOldQuantity + adjustment;
                  
                  // 🔴 DAMAGED WRITE-OFF FOR SIZE
                  if (adjustmentReason === 'Damaged items (write-off) - Move to damaged' && adjustment < 0) {
                    const damagedIncrease = Math.abs(adjustment);
                    const updatedSizes = [...(product.ukShoeSizes || [])];
                    updatedSizes[editingStock.sizeIndex] = { 
                      ...size, 
                      stockQuantity: sizeNewTotal 
                    };
                    
                    updateStock(
                      product._id,
                      { 
                        ukShoeSizes: updatedSizes,
                        damagedQuantity: (product.damagedQuantity || 0) + damagedIncrease
                      },
                      {
                        productId: product._id,
                        productName: product.name,
                        productIdentifier: product.productId,
                        type: 'size',
                        identifier: `UK Size: ${size.ukSize}`,
                        oldQuantity: sizeOldQuantity,
                        newQuantity: sizeNewTotal,
                        change: adjustment,
                        reason: adjustmentReason
                      }
                    );
                    return;
                  }
                  
                  // ✅ NORMAL SIZE UPDATE
                  const updatedSizes = [...(product.ukShoeSizes || [])];
                  updatedSizes[editingStock.sizeIndex] = { 
                    ...size, 
                    stockQuantity: sizeNewTotal 
                  };
                  
                  // If this is a new shipment for this size
                  if (adjustmentReason === 'New shipment received' && adjustment > 0) {
                    updatedSizes[editingStock.sizeIndex] = { 
                      ...size, 
                      stockQuantity: sizeNewTotal,
                      totalAcquired: (size.totalAcquired || 0) + adjustment
                    };
                  }
                  
                  updateStock(
                    product._id,
                    { ukShoeSizes: updatedSizes },
                    {
                      productId: product._id,
                      productName: product.name,
                      productIdentifier: product.productId,
                      type: 'size',
                      identifier: `UK Size: ${size.ukSize}`,
                      oldQuantity: sizeOldQuantity,
                      newQuantity: sizeNewTotal,
                      change: adjustment,
                      reason: adjustmentReason
                    }
                  );
                  return;
                }
              }}>
                Save Changes
              </button>
              <button className="cancel-btn" onClick={() => {
                setEditingStock(null);
                setAdjustmentAmount('');
                setAdjustmentReason('');
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturnModal && returnItem && (
        <div className="stock-edit-modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="stock-edit-modal" onClick={e => e.stopPropagation()}>
            <h3>🔄 Process Return</h3>
            <div className="form-group">
              <label>Product:</label>
              <input type="text" value={`${returnItem.productName} (${returnItem.productIdentifier.slice(-8)})`} disabled />
            </div>
            <div className="form-group">
              <label>Quantity:</label>
              <input
                type="number"
                value={returnQuantity}
                onChange={(e) => setReturnQuantity(parseInt(e.target.value) || 1)}
                min="1"
                max={returnItem.currentStock}
              />
            </div>
            <div className="form-group">
              <label>Return Quality:</label>
              <div className="return-quality-options">
                <label className={`quality-option ${returnQuality === 'good' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="good"
                    checked={returnQuality === 'good'}
                    onChange={() => setReturnQuality('good')}
                  />
                  <span>✅ Good (Return to stock for resell)</span>
                </label>
                <label className={`quality-option ${returnQuality === 'bad' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="bad"
                    checked={returnQuality === 'bad'}
                    onChange={() => setReturnQuality('bad')}
                  />
                  <span>❌ Bad (Move to damaged)</span>
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Return Reason:</label>
              <select value={returnReason} onChange={(e) => setReturnReason(e.target.value)}>
                <option value="">Select reason...</option>
                <option value="Customer changed mind">Customer changed mind</option>
                <option value="Wrong size">Wrong size</option>
                <option value="Defective product">Defective product</option>
                <option value="Damaged during shipping">Damaged during shipping</option>
                <option value="Wrong item sent">Wrong item sent</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {returnReason === 'Other' && (
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Please specify reason..."
                  onChange={(e) => setReturnReason(e.target.value)}
                />
              </div>
            )}
            <div className="modal-actions">
              <button className="save-btn" onClick={handleReturn}>
                Process Return
              </button>
              <button className="cancel-btn" onClick={() => setShowReturnModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start Quantity Modal */}
      {showStartQuantityModal && startQuantityProduct && (
        <div className="stock-edit-modal-overlay" onClick={() => setShowStartQuantityModal(false)}>
          <div className="stock-edit-modal" onClick={e => e.stopPropagation()}>
            <h3>📊 Set Baseline Stock Level</h3>
            <div className="form-group">
              <label>Product:</label>
              <input type="text" value={`${startQuantityProduct.name} (${startQuantityProduct.productId.slice(-8)})`} disabled />
            </div>
            <div className="form-group">
              <label>Current Stock:</label>
              <input type="number" value={startQuantityProduct.stockQuantity || 0} disabled />
            </div>
            <div className="form-group">
              <label>Set Baseline Stock Quantity:</label>
              <input
                type="number"
                value={startQuantityValue}
                onChange={(e) => setStartQuantityValue(e.target.value)}
                min="0"
                autoFocus
              />
              <small className="info-text">
                <strong>ℹ️ This is your REFERENCE POINT</strong> - it will NOT change when orders are fulfilled.
                Use this to track: Start Qty - Current Stock = Total Units Sold
              </small>
            </div>
            
            {/* Show sales tracking info */}
            {startQuantityProduct.startQuantity > 0 && (
              <div className="stock-tracking-info">
                <h4>📈 Sales Tracking</h4>
                <div className="tracking-stats">
                  <div className="tracking-item">
                    <span>Baseline:</span>
                    <strong>{startQuantityProduct.startQuantity} units</strong>
                  </div>
                  <div className="tracking-item">
                    <span>Current Stock:</span>
                    <strong>{startQuantityProduct.stockQuantity || 0} units</strong>
                  </div>
                  <div className="tracking-item">
                    <span>Units Sold:</span>
                    <strong style={{ color: '#2196f3' }}>
                      {(startQuantityProduct.startQuantity - (startQuantityProduct.stockQuantity || 0))} units
                    </strong>
                  </div>
                  <div className="tracking-item">
                    <span>Stock Remaining:</span>
                    <strong style={{ 
                      color: ((startQuantityProduct.stockQuantity || 0) / startQuantityProduct.startQuantity) < 0.2 ? '#f44336' : '#4caf50'
                    }}>
                      {((startQuantityProduct.stockQuantity || 0) / startQuantityProduct.startQuantity * 100).toFixed(1)}%
                    </strong>
                  </div>
                </div>
              </div>
            )}
            
            <div className="modal-actions">
              <button className="save-btn" onClick={handleStartQuantityUpdate}>
                Set Baseline
              </button>
              <button className="cancel-btn" onClick={() => setShowStartQuantityModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockManager;